import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Cruise, Flights, Hotel, Rental, Wishlist } from '../../assests/ExportImageIcon'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import './wishlist.css'

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

// record_module -> type badge icon (falls back to the generic wishlist heart
// for modules we don't have a dedicated icon for).
const TYPE_ICONS = {
    flight: Flights,
    hotel: Hotel,
    car: Rental,
    cruise: Cruise,
}

const typeIcon = (recordModule) => {
    const key = String(recordModule || '').toLowerCase().replace(/s$/, '')
    return TYPE_ICONS[key] || Wishlist
}

const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1)

const SelectItems = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const wishlistId = location.state?.wishlistId
    const shareLink = location.state?.shareLink

    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeFilter, setActiveFilter] = useState('all')
    const [selectedIds, setSelectedIds] = useState(
        () => new Set((location.state?.selectedItems || []).map((item) => item.id)),
    )

    useEffect(() => {
        if (!wishlistId) {
            setLoading(false)
            return
        }
        setLoading(true)
        // Items to pick from live in the user's own individual (default) wishlist
        // — there's no cross-wishlist "all my favorites" endpoint, so resolve its
        // id from the list first, then fetch its items.
        service
            .get(endpoints.wishlists.list, {}, { silent: true })
            .then(({ data }) => {
                const all = Array.isArray(data.data) ? data.data : []
                const individual = all.find((w) => w.type === 'individual')
                if (!individual?.id) return { data: { data: [] } }
                return service.get(
                    endpoints.wishlists.items.replace('{wishlist_id}', individual.id),
                    { page_size: 100 },
                    { silent: true },
                )
            })
            .then(({ data }) => setItems(Array.isArray(data?.data) ? data.data : []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false))
    }, [wishlistId])

    const filterTabs = useMemo(() => {
        const counts = {}
        items.forEach((item) => {
            const key = String(item.record_module || 'other').toLowerCase()
            counts[key] = (counts[key] || 0) + 1
        })
        return [
            { key: 'all', label: `All (${items.length})` },
            ...Object.keys(counts).map((key) => ({ key, label: `${titleCase(key)} (${counts[key]})` })),
        ]
    }, [items])

    const filteredItems = activeFilter === 'all'
        ? items
        : items.filter((item) => String(item.record_module || '').toLowerCase() === activeFilter)

    const allSelected = filteredItems.length > 0 && filteredItems.every((item) => selectedIds.has(item.id))

    const toggleItem = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const toggleSelectAll = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (allSelected) {
                filteredItems.forEach((item) => next.delete(item.id))
            } else {
                filteredItems.forEach((item) => next.add(item.id))
            }
            return next
        })
    }

    const handleConfirm = () => {
        const selectedItems = items.filter((item) => selectedIds.has(item.id))
        navigate('/add-wishlist-item', { state: { wishlistId, selectedItems, shareLink } })
    }

    if (!wishlistId) {
        return (
            <section className='allSearchArea commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='fare-extra-hint'>No wishlist in progress. Please start from Create Wishlist.</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/create-wishlist')}>Create Wishlist</button>
                </div>
            </section>
        )
    }

    return (
        <section className='allSearchArea commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='row g-4'>
                    <div className='col-md-12'>
                        <div className='commonMaxWidth'>
                            <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                <BackArrow />
                                <span className='bsSectionTitle'>Select Items</span>
                            </div>
                        </div>
                    </div>

                    <div className='col-md-12'>
                        <div className='commonBookingTab commonMaxWidth bgWhite paddingCommon20 borderRadius20'>
                            {filterTabs.map((tab) => (
                                <div
                                    key={tab.key}
                                    className={`bookTab ${activeFilter === tab.key ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(tab.key)}
                                >
                                    {tab.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className='col-md-12'>
                        <div className='commonMaxWidth bgWhite paddingCommon20 borderRadius20'>
                            {loading ? (
                                <p className='fare-extra-hint'>Loading your saved items...</p>
                            ) : !filteredItems.length ? (
                                <p className='fare-extra-hint'>No saved items yet. Fav some flights, hotels or cars first, then come back here.</p>
                            ) : (
                                <>
                                    <div className='selectItemsCount'>
                                        <span className='selectItemsCount__label'>
                                            {filteredItems.length} {filteredItems.length === 1 ? 'Item' : 'Items'} found
                                        </span>
                                        <label className='selectItemsCount__selectAll'>
                                            Select all
                                            <input type='checkbox' className='wishlistCard__checkbox' checked={allSelected} onChange={toggleSelectAll} />
                                        </label>
                                    </div>

                                    <div className='wishlistPage__list'>
                                        {filteredItems.map((item) => (
                                            <div className='wishlistCard commonBorder borderRadius20' key={item.id}>
                                                <div className='wishlistCard__media'>
                                                    {item.image_url ? <img src={item.image_url} alt={item.record_name} /> : null}
                                                    <div className='wishlistCard__mediaBadges'>
                                                        <span className='wishlistCard__typeBadge'>
                                                            <img src={typeIcon(item.record_module)} alt="" />
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className='wishlistCard__info'>
                                                    <div className='hotelCard__name'>{item.record_name || 'Saved item'}</div>
                                                </div>

                                                <input
                                                    type='checkbox'
                                                    className='wishlistCard__checkbox'
                                                    aria-label="Select"
                                                    checked={selectedIds.has(item.id)}
                                                    onChange={() => toggleItem(item.id)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {!loading && filteredItems.length > 0 && (
                        <div className='col-md-12'>
                            <div className='commonMaxWidth'>
                                <button type='button' className='commonBtn w-100' onClick={handleConfirm} disabled={selectedIds.size === 0}>
                                    Add {selectedIds.size > 0 ? selectedIds.size : ''} Selected {selectedIds.size === 1 ? 'Item' : 'Items'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export default SelectItems
